"use client";

import { useEffect, useRef } from "react";

import { validateInviteCode } from "@features/invite/validateInviteCode";
import {
  captureInviteCodeFromLocation,
  clearInviteCode,
  restoreInviteCodeFromCookie,
} from "@utils/invite/inviteCodeCookie.client";
import { getInviteCodeFromStore } from "@utils/invite/inviteCodeStore";
import {
  trackInviteLanding,
  trackLegacyInviteLinkOpen,
} from "@utils/invite/inviteLandingTracking";
import {
  extractInviteCodeFromPathname,
  INVITE_CODE_QUERY_KEY,
  normalizeInviteCode,
} from "@utils/invite/inviteCode";
import type { InviteValidationRequester } from "@features/invite/validateInviteCode";

interface UseInviteCodeCaptureArgs {
  /** 当前 pathname（`usePathname()`）—— `/task/invite/<code>` 由此解出码。 */
  pathname: string | null;
  /**
   * 当前 query 串（含 `?`）—— `?inviteCode=` 由此解出码。
   *
   * **必须作为独立入参而非在内部读 `window.location`**：它同时是 effect 的依赖，
   * 少了它，「同一 pathname 下只换 query」的跳转不会重跑捕获，
   * 于是回落到 cookie，把**上一个**码灌回 store（表现为「新码覆盖不掉旧码」）。
   */
  search: string;
  /**
   * 登录态；还在确定中（`LoginStore.isFetching`）时由调用方传 undefined ——
   * 此时不抓码，否则已登录用户会先被写进 cookie、确认登录后再清掉。
   */
  isLoggedIn: boolean | undefined;
  memberApi: InviteValidationRequester | null | undefined;
}

/**
 * `unknown` 时的重试上限（含首次共 1 + N 次）。
 *
 * `unknown` = 429 限流 / 网络失败 / `memberApi` 尚未就绪，是「判不了」而非结论：
 * 码先乐观保留，但不能像 valid/invalid 那样记为已校验 —— 否则一个**无效码**只要首次
 * 没答上来就永远清不掉。也不能无限重试，否则站内每跳一次都打一发，
 * 反过来加剧限流。给个小上限即可覆盖「api 刚就绪」这个主要场景。
 */
const UNKNOWN_REVALIDATE_LIMIT = 2;

/**
 * 邀请码捕获：`/task/invite/<code>` path 段优先，其次 `?inviteCode=` query → cookie + store。
 * 本次地址没带码时，把上次会话 cookie 里的码灌回 store（「上次点邀请链接、这次才来注册」）。
 * 链接码被后端判无效 → 清码，入口回到「Have an invite code?」（fe-spec D7）。
 *
 * 注册请求头 `OS-Invite-Code` 由此取值（见 `adjustRequestHeaders.mergeAdjustAdidIntoHeaders`）。
 *
 * 注意 path 形态能走到这里的前提是 Proxy 放行了邀请落地页
 * （`isInviteLandingPath`）—— 否则 307 会把码连同路径一起丢掉。
 */
export function useInviteCodeCapture({
  pathname,
  search,
  isLoggedIn,
  memberApi,
}: UseInviteCodeCaptureArgs): void {
  /**
   * 已拿到**确定结论**（valid / invalid）的邀请码 —— 防止站内每次路由变化都重打
   * /auth/validate（有限流）。`unknown` 不记进来，见 {@link UNKNOWN_REVALIDATE_LIMIT}。
   */
  const validatedInviteCodeRef = useRef<string | null>(null);
  /** 当前码在 `unknown` 上已重试的次数，换码时归零。 */
  const unknownRetryRef = useRef<{ code: string | null; count: number }>({
    code: null,
    count: 0,
  });
  /**
   * 正在校验中的码 —— **同步**去重，防止结论还没回来时的路由变化重复打
   * `/auth/validate`（落地页 → /login 的跳转常常就发生在请求 in-flight 期间）。
   * 结论回来即清空：确定结论转由 `validatedInviteCodeRef` 长期封存，
   * `unknown` 则解锁以便重试。
   */
  const inFlightInviteCodeRef = useRef<string | null>(null);
  /**
   * 已经从地址里抓过的码 —— 同一个地址码只抓一次。
   * 用户在 `/login?inviteCode=X` 上手动改码或清空后，地址里的 X 还在；
   * 不挡住的话，任何一次 pathname 变化都会把 X 重新写回，用户的手动操作就被覆盖了。
   */
  const consumedLocationCodeRef = useRef<string | null>(null);
  /**
   * 来自旧 Missions 邀请链接（`/task/invite/<code>`）、尚未上报 `inv_legacy_link_open` 的码。
   * 落地后会被跳到 `/login?inviteCode=<code>`，同一个码只校验一次，所以要在抓码时记下来源。
   */
  const pendingLegacyCodeRef = useRef<string | null>(null);

  useEffect(() => {
    // 登录态未知 → 先不动，等结论出来（effect 依赖 isLoggedIn，会重跑）
    if (isLoggedIn === undefined) {
      return;
    }
    const locationCode = readInviteCodeFromLocation(pathname, search);
    const freshLocationCode =
      locationCode !== null && locationCode !== consumedLocationCodeRef.current;
    // 只记最近一次抓过的码，路过不带码的地址不清空 —— 否则浏览器返回到原链接会再抓一次
    if (locationCode !== null) {
      consumedLocationCodeRef.current = locationCode;
    }
    // 带链接码落地，每个地址码只报一次（fe-spec §3.9.1 inv_landing_view）
    if (freshLocationCode) {
      trackInviteLanding(isLoggedIn ? "existing" : "new");
      // 旧 Missions 邀请链接：已登录时不校验（直接回首页），只在未登录时等校验结论上报 matched
      if (
        !isLoggedIn &&
        extractInviteCodeFromPathname(pathname) === locationCode
      ) {
        pendingLegacyCodeRef.current = locationCode;
      }
    }
    // 已登录 → 清掉本地邀请码：它只对**首次注册**生效，留着不会再绑定，
    // 却会在同设备换账号注册时误带上一个人的码。
    if (isLoggedIn) {
      clearInviteCode();
      validatedInviteCodeRef.current = null;
      inFlightInviteCodeRef.current = null;
      unknownRetryRef.current = { code: null, count: 0 };
      return;
    }
    const active = freshLocationCode
      ? captureInviteCodeFromLocation(pathname, search)
      : restoreInviteCodeFromCookie();
    if (!active) {
      return;
    }
    // 已有确定结论的码不再打网络：本 effect 依赖 pathname + search，站内每次跳转都会重跑，
    // 不去重会把 /auth/validate 的设备级限流打爆。
    if (validatedInviteCodeRef.current === active) {
      return;
    }
    // 同一个码的请求还在路上 → 不重复打
    if (inFlightInviteCodeRef.current === active) {
      return;
    }
    // 换码 → unknown 重试计数归零
    if (unknownRetryRef.current.code !== active) {
      unknownRetryRef.current = { code: active, count: 0 };
    } else if (unknownRetryRef.current.count >= UNKNOWN_REVALIDATE_LIMIT) {
      return;
    }
    unknownRetryRef.current.count += 1;
    inFlightInviteCodeRef.current = active;

    void validateInviteCode(memberApi, active).then((result) => {
      // 后端明确判无效 → 当作没有码（D7）。unknown 乐观保留，注册时后端会再判一次。
      // 只清**这个**码：请求在路上时用户可能已经手动换了码，不能误删。
      if (result.status === "invalid" && getInviteCodeFromStore() === active) {
        clearInviteCode();
      }
      // 旧 Missions 邀请链接：有确定结论才报，每个码一次（PRD S2.10）
      if (
        result.status !== "unknown" &&
        pendingLegacyCodeRef.current === active
      ) {
        trackLegacyInviteLinkOpen(result.status === "valid");
        pendingLegacyCodeRef.current = null;
      }
      inFlightInviteCodeRef.current = null;
      if (result.status !== "unknown") {
        // 只有确定结论才封存；unknown 留待下次路由变化 / memberApi 就绪后重试
        validatedInviteCodeRef.current = active;
      }
    });
  }, [pathname, search, isLoggedIn, memberApi]);
}

/** 地址里的邀请码（path 段优先，其次 query），只读不写。 */
function readInviteCodeFromLocation(
  pathname: string | null,
  search: string,
): string | null {
  return (
    extractInviteCodeFromPathname(pathname) ??
    normalizeInviteCode(new URLSearchParams(search).get(INVITE_CODE_QUERY_KEY))
  );
}
